import React, { FC, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { ButtonTypes, IconButton } from '@eika/button';
import { CheckboxGroup } from '@eika/checkbox';
import { CurrencyInput, InputError } from '@eika/currency-input';
import { IconTypes } from '@eika/icon';
import { ButtonToggles } from '@eika/radio-button';

import { EgenkapitalType, ValgtWeb } from '../../generated-types';
import { useTranslation } from '../../translations';
import { EgenkapitalFormData } from './egenkapital';
import EgenkapitalInfoBox, { EgenkapitalInfoType, EgenkapitalModalParam } from './egenkapitalInfoBox';

const EgenkapitalAnnen: FC = () => {
    const { egenkapital, button, validationMessage } = useTranslation();
    const {
        setValue,
        control,
        formState: { errors },
    } = useFormContext<EgenkapitalFormData>();
    const [egenkapitalModalParam, setShowModal] = useState<EgenkapitalModalParam>({
        showModal: false,
        egenkapitalInfoType: EgenkapitalInfoType.UKJENT,
    });
    const harAnnenEgenkapital = useWatch({ name: 'harAnnenEgenkapital', control });
    const annenEgenkapitalSum = useWatch({ name: 'annenEgenkapitalSum', control });
    const egenkapitalKilde = useWatch({ name: 'kilde', control });

    const onChangeHarAnnenEgenkapital = (value: ValgtWeb) => {
        if (value === ValgtWeb.NEI) {
            setValue('annenEgenkapitalSum', undefined);
            setValue('kilde', [], { shouldValidate: false });
        }
        setValue('harAnnenEgenkapital', value, { shouldValidate: true });
    };

    const onChangeEgenkapitalKilde = (checked: boolean, value: string) => {
        if (checked) {
            egenkapitalKilde.push(value as EgenkapitalType);
        } else {
            egenkapitalKilde.splice(
                egenkapitalKilde.findIndex((e) => e === (value as EgenkapitalType)),
                1,
            );
        }
        setValue('kilde', egenkapitalKilde, { shouldValidate: true });
    };

    return (
        <>
            <EgenkapitalInfoBox egenkapitalModalParam={egenkapitalModalParam} setShowModal={setShowModal} />
            <Controller
                name="harAnnenEgenkapital"
                control={control}
                rules={{
                    validate: { ikkeValgtAnnenEgenkapital: (value: ValgtWeb) => value !== ValgtWeb.IKKE_VALGT },
                }}
                render={({ field: { value } }) => (
                    <ButtonToggles
                        label={egenkapital.togglesEgenkapital}
                        data-track-id="kreditt-boliglaan-egenkapital-valg-har-annen-egenkapital"
                        data-track-linkname={value}
                        infoButton={
                            <IconButton
                                className="sgw-radio-icon"
                                icon={IconTypes.INFO_24}
                                buttonType={ButtonTypes.ICON}
                                screenReaderText={button.iconButtonInformasjon}
                                onClick={() =>
                                    setShowModal({
                                        showModal: true,
                                        egenkapitalInfoType: EgenkapitalInfoType.HAR_EGENKAPITAL,
                                    })
                                }
                            />
                        }
                        onChange={(value) => onChangeHarAnnenEgenkapital(value as ValgtWeb)}
                        selectedOption={value}
                        options={[
                            { label: button.ja, value: ValgtWeb.JA },
                            { label: button.nei, value: ValgtWeb.NEI },
                        ]}
                        errorMessage={
                            errors.harAnnenEgenkapital?.type === 'ikkeValgtAnnenEgenkapital'
                                ? `${validationMessage.requiredChoose}`
                                : ''
                        }
                    />
                )}
            />
            {harAnnenEgenkapital === ValgtWeb.JA && (
                <>
                    <Controller
                        name="annenEgenkapitalSum"
                        control={control}
                        rules={{
                            required: `${validationMessage.requiredValue}`,
                        }}
                        render={({ field: { onChange } }) => (
                            <CurrencyInput
                                name="annenEgenkapitalSum"
                                label={egenkapital.labelEgenkapital}
                                sublabel={egenkapital.subLabelEgenkapital}
                                className="max-width-200"
                                placeholder=""
                                type="tel"
                                error={errors.annenEgenkapitalSum?.message}
                                value={annenEgenkapitalSum}
                                onValueChange={(v) => onChange(v.value)}
                            />
                        )}
                    />
                    <Controller
                        name="kilde"
                        control={control}
                        rules={{
                            validate: { kildeIkkeSatt: (value) => value.length > 0 },
                        }}
                        render={() => (
                            <CheckboxGroup
                                key="egenkapitalKilde"
                                label={egenkapital.checkboxEgenkapital.title}
                                className="margin-bottom-small"
                                infoButton={
                                    <IconButton
                                        className="sgw-radio-icon"
                                        icon={IconTypes.INFO_24}
                                        buttonType={ButtonTypes.ICON}
                                        screenReaderText={button.iconButtonInformasjon}
                                        onClick={() =>
                                            setShowModal({
                                                showModal: true,
                                                egenkapitalInfoType: EgenkapitalInfoType.HVOR_EGENKAPTIAL_KOMMER_FRA,
                                            })
                                        }
                                    />
                                }
                                options={[
                                    {
                                        label: egenkapital.checkboxEgenkapital.loenn,
                                        value: EgenkapitalType.LOENN,
                                        checked: egenkapitalKilde.some((e) => e === EgenkapitalType.LOENN),
                                        onValueChange: (checked, value) => {
                                            onChangeEgenkapitalKilde(checked, value);
                                        },
                                    },
                                    {
                                        label: egenkapital.checkboxEgenkapital.pensjon,
                                        value: EgenkapitalType.PENSJON,
                                        checked: egenkapitalKilde.some((e) => e === EgenkapitalType.PENSJON),
                                        onValueChange: (checked, value) => {
                                            onChangeEgenkapitalKilde(checked, value);
                                        },
                                    },
                                    {
                                        label: egenkapital.checkboxEgenkapital.sparing,
                                        value: EgenkapitalType.SPARING,
                                        checked: egenkapitalKilde.some((e) => e === EgenkapitalType.SPARING),
                                        onValueChange: (checked, value) => {
                                            onChangeEgenkapitalKilde(checked, value);
                                        },
                                    },
                                    {
                                        label: egenkapital.checkboxEgenkapital.trygd,
                                        value: EgenkapitalType.TRYGD,
                                        checked: egenkapitalKilde.some((e) => e === EgenkapitalType.TRYGD),
                                        onValueChange: (checked, value) => {
                                            onChangeEgenkapitalKilde(checked, value);
                                        },
                                    },
                                    {
                                        label: egenkapital.checkboxEgenkapital.arvEllerGave,
                                        value: EgenkapitalType.ARVGAVE,
                                        checked: egenkapitalKilde.some((e) => e === EgenkapitalType.ARVGAVE),
                                        onValueChange: (checked, value) => {
                                            onChangeEgenkapitalKilde(checked, value);
                                        },
                                    },
                                    {
                                        label: egenkapital.checkboxEgenkapital.annen,
                                        value: EgenkapitalType.ANNET,
                                        checked: egenkapitalKilde.some((e) => e === EgenkapitalType.ANNET),
                                        onValueChange: (checked, value) => {
                                            onChangeEgenkapitalKilde(checked, value);
                                        },
                                    },
                                ]}
                            />
                        )}
                    />
                    {errors.kilde?.type === 'kildeIkkeSatt' && <InputError error={validationMessage.requiredChoose} />}
                </>
            )}
        </>
    );
};

export default EgenkapitalAnnen;
